// CategoryPage.js
// Displays a learning category with its study material and associated questions.
// Users can view, add, edit, delete questions, and edit the study material.

import React, { useState, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Fab,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";

import {
  getQuestions,
  getCategoryById,
  updateMaterial,
  updateCategoryName, //For updating category name - send: updateCategoryName(categoryId, newName)
  deleteCategory, //Deletes the category - send: deleteCategory(categoryId)
  deleteQuestion, //Deletes a question - send deleteQuestion(categoryId, questionId)
} from "../../firebase-handler.js";

import "../CSS/CategoryPage.css";

// Component for displaying and managing a single learning category
const CategoryPage = () => {
  const { categoryId } = useParams(); // Get category ID from route
  const navigate = useNavigate();

  // State for category and its questions
  const [category, setCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state for dialogs, snackbar, material editing
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMaterialValue, setEditMaterialValue] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  // Fetch category data and its questions when component mounts or categoryId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!categoryId) {
        setError("Category ID is missing");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const categoryData = await getCategoryById(categoryId);
        if (!categoryData) {
          throw new Error("Category not found");
        }
        setCategory(categoryData);

        const questionsData = await getQuestions(categoryId);

        const sortedQuestions = (questionsData || [])
          .map((q) => ({
            ...q,
            showAnswer: false,
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort newest first

        setQuestions(sortedQuestions);
      } catch (err) {
        console.error("Error fetching category data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  // Navigate to Add Question screen
  const handleAddQuestion = () => {
    navigate(`/categories/${categoryId}/add`);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (questionId) => {
    setQuestionToDelete(questionId);
    setDeleteDialogOpen(true);
  };

  // Confirm and delete question from Firestore
  const confirmDelete = async () => {
    try {
      await deleteQuestion(categoryId, questionToDelete);
      setQuestions((prev) => prev.filter((q) => q.id !== questionToDelete));
      setSnackbarMessage("השאלה נמחקה בהצלחה");
      setSnackbarSeverity("success");
    } catch (err) {
      console.error("Error deleting question:", err);
      setSnackbarMessage("שגיאה במחיקת השאלה");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
    }
  };

  // Cancel question delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setQuestionToDelete(null);
  };

  // Open dialog to edit material
  const handleEditMaterial = () => {
    setEditMaterialValue(category?.material || "");
    setEditDialogOpen(true);
  };

  // Save updated material to Firestore
  const handleSaveMaterial = async () => {
    try {
      await updateMaterial(categoryId, editMaterialValue);
      setSnackbarMessage("חומר הלימוד נשמר בהצלחה!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Update our local state so the UI shows the new material
      setCategory((prev) => ({ ...prev, material: editMaterialValue }));

      setEditDialogOpen(false);
    } catch (err) {
      console.error("Error updating material: ", err);
      setSnackbarMessage("שגיאה בשמירת חומר הלימוד.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Cancel editing material
  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditMaterialValue(category?.material || "");
    setSnackbarMessage("עריכת חומר הלימוד בוטלה");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  // Snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // --- UI rendering starts here ---

  // Show loading spinner
  if (loading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 20 }}>
        <CircularProgress />
      </Container>
    );
  }

  // Show error message
  if (error) {
    return (
      <Container sx={{ mt: 15 }} dir="rtl">
        <Typography color="error" align="center">
          שגיאה בטעינת הנתונים: {error}
        </Typography>
      </Container>
    );
  }

  // No category found
  if (!category) {
    return (
      <Container sx={{ mt: 15 }} dir="rtl">
        <Typography variant="h5" align="center">
          קטגוריה לא נמצאה.
        </Typography>
      </Container>
    );
  }

  const isHebrew = (text) => /[\u0590-\u05FF]/.test(text);
  const materialDirection = isHebrew(editMaterialValue) ? "rtl" : "ltr";

  // Main layout
  return (
    <Box
      sx={{
        mt: 14,
        px: 4, // optional outer padding
        height: "calc(100vh - 112px)",
        overflow: "auto",
        width: "100%",
      }}
      dir="rtl"
    >
      {/* Category name */}
      <Typography
        variant="h3"
        component="h1"
        align="center"
        gutterBottom
        sx={{ mb: 4 }}
      >
        {category?.name || "טוען שם קטגוריה..."}
      </Typography>
      <Grid container spacing={3} sx={{ height: "75%", alignItems: "stretch", flexWrap: "nowrap" }}>
        {/* --- Questions Panel --- */}
        <Grid item xs={12} md={6} sx={{ height: "100%", display: "flex", flexDirection: "column", flexGrow: 1, flexBasis: 0, pl: 2, }}>
          <Paper elevation={3} sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", position: "relative", p: 2, pb: 8,}}>
            <Typography variant="h5" gutterBottom sx={{ textAlign: "center" }}> שאלות ביחידה </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* List of questions */}
            {questions.length > 0 ? (
              <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                <List>
                  {questions.map((question, index) => (
                    <React.Fragment key={question.id}>
                      <ListItem
                        sx={{
                          flexDirection: "column",
                          alignItems: "flex-start",
                          px: 2,
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          {/* Click to edit question */}
                          <ListItemText
                            primaryTypographyProps={{
                              fontSize: 16,
                              fontWeight: 500,
                              sx: { whiteSpace: "pre-line" },
                            }}
                            sx={{ cursor: "pointer", textAlign: "right" }}
                            primary={question.question || "טקסט שאלה חסר"}
                            onClick={() =>
                              navigate(
                                `/categories/${categoryId}/edit/${question.id}`,
                                {
                                  state: {
                                    question: {
                                      ...question,
                                      category: categoryId,
                                    },
                                  },
                                }
                              )
                            }
                          />

                          {/* Expand/collapse answer */}
                          <IconButton
                            onClick={() =>
                              setQuestions((prev) =>
                                prev.map((q, i) =>
                                  i === index
                                    ? { ...q, showAnswer: !q.showAnswer }
                                    : q
                                )
                              )
                            }
                          >
                            <ArrowForwardIosIcon
                              sx={{
                                transform: question.showAnswer
                                  ? "rotate(270deg)"
                                  : "rotate(90deg)",
                                transition: "transform 0.2s ease-in-out",
                              }}
                            />
                          </IconButton>
                          <Box>
                            {/* Delete question button */}
                            <Fab
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(question.id)}
                              sx={{ ml: 1 }}
                            >
                              {" "}
                              <DeleteIcon fontSize="small" />
                            </Fab>
                          </Box>
                        </Box>

                        {/* Collapsible answer section */}
                        {question.showAnswer && (
                          <Box
                            dir="rtl"
                            sx={{
                              mt: 1,
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              bgcolor: "action.hover",
                              width: "100%",
                              textAlign: "right",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ whiteSpace: "pre-line" }}>
                              {question.answer}
                            </Typography>
                          </Box>
                        )}
                      </ListItem>
                      <Divider sx={{ my: 1 }} />
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography sx={{ textAlign: "center", mt: 4, flexGrow: 1 }}>
                עדיין אין שאלות ביחידה זו.
              </Typography>
            )}

            {/* Add new question button */}
            <Tooltip title="הוסף שאלה חדשה">
              <Fab
                color="primary"
                aria-label="add question"
                onClick={handleAddQuestion}
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 16,
                }}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          </Paper>
        </Grid>

        {/* --- Study Material Panel --- */}
        <Grid item xs={12} md={6} sx={{ height: "100%", display: "flex", flexDirection: "column", flexGrow: 1, flexBasis: 0, ml: 7, }}>
          <Paper elevation={3} sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", position: "relative", p: 2, pb: 8, pr: 2, pl: 7, }} >
            <Typography variant="h5" gutterBottom sx={{ textAlign: "center" }}> חומר לימוד </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 1, height: "100%", wordBreak: "break-word", }} >
              {/* Editable or static material view */}
              {editDialogOpen ? (
                <TextField 
                  multiline
                  fullWidth
                  value={editMaterialValue}
                  onChange={(e) => setEditMaterialValue(e.target.value)}
                  sx={{ flexGrow: 1, overflow: "auto", display: "flex", height: "100%", }}
                  InputProps={{
                    sx: { height: "100%", alignItems: "flex-start", },
                    inputProps: {
                      style: {
                        direction: materialDirection,
                        textAlign: materialDirection === "rtl" ? "right" : "left",
                      },
                    },
                  }}
                />
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    textAlign: isHebrew(category?.material || "")
                      ? "right"
                      : "left",
                    direction: isHebrew(category?.material || "")
                      ? "rtl"
                      : "ltr",
                    flexGrow: 1,
                    overflow: "auto",
                  }}
                >
                  {category?.material || "אין חומר לימוד זמין."}
                </Typography>
              )}
            </Box>

            {/* Edit / Save / Cancel buttons */}
            {!editDialogOpen ? (
              <Tooltip title="ערוך חומר לימוד">
                <Fab
                  color="secondary"
                  aria-label="edit material"
                  onClick={() => {
                    setEditMaterialValue(category?.material || "");
                    setEditDialogOpen(true);
                  }}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                  }}
                >
                  <EditIcon />
                </Fab>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="שמור">
                  <Fab
                    color="primary"
                    aria-label="save"
                    onClick={handleSaveMaterial}
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      left: 96,
                    }}
                  >
                    <SaveIcon />
                  </Fab>
                </Tooltip>
                <Tooltip title="בטל">
                  <Fab
                    color="error"
                    aria-label="cancel"
                    onClick={handleCancelEdit}
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      left: 16,
                    }}
                  >
                    ✖
                  </Fab>
                </Tooltip>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Confirm delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>אישור מחיקה</DialogTitle>
        <DialogContent>האם אתה בטוח שברצונך למחוק את השאלה?</DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>בטל</Button>
          <Button onClick={confirmDelete} color="error">
            מחק
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={handleCloseSnackbar}
          sx={{
            width: "100%",
            // Increase the font size and padding for better visibility:
            fontSize: "1.25rem",
            py: 2,
            px: 3,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryPage;